
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, Subject, Observable } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { DataSelectDialogServiceInterface, IncludeOrExclude } from '../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { LanguageModel } from '../../../Core/Localization/models/language.model';

/**
 * This service is the implementation of the interface for the DataSelectDialogComponent.
 *
 * It defines the logic for handling the selection and removal of languages models to either the include or exclude
 * languages on the MonitoringSearchQuery.
 */
@Injectable()
export abstract class LanguageSelectDialogService implements DataSelectDialogServiceInterface, OnDestroy {
    public readonly languageTreeNodeType: string = 'language';

    treeNodes: TreeNode[];
    treeNodesProvider: BehaviorSubject<TreeNode[]> = new BehaviorSubject(null);

    dialiogVisibilityProvider: Subject<boolean> = new Subject();

    selectedListItemProvider: BehaviorSubject<TreeNode[]> = new BehaviorSubject(null);
    selectedItemTypes: Map<string, string> = new Map<string, string>([
        [this.languageTreeNodeType, 'Monitoring.search.languages']
    ]);
    selectedItemTypesProvider: BehaviorSubject<Map<string, string>>;

    selectedDataItems: TreeNode[];
    selectedDataItemsProvider: BehaviorSubject<TreeNode[]>;

    selectedLanguages: LanguageModel[];
    selectedListItems: TreeNode[] = [];
    subscriptions: Subscription[] = [];
    state: IncludeOrExclude;

    constructor(
        protected localizationService: LocalizationService,
    ) {
        this.selectedItemTypesProvider = new BehaviorSubject<Map<string, string>>(this.selectedItemTypes);
        this.selectedDataItemsProvider = new BehaviorSubject<TreeNode[]>(null);

        this.subscriptions.push(
            this.localizationService.getAvailableLanguagesByDataReference().subscribe(languages => {
                this.treeNodes = this.convertDataToTreeNodes(languages);
                this.treeNodesProvider.next(this.treeNodes);
            })
        );
    }


    abstract applySelectedListItems(selectedItems: TreeNode[]);
    abstract setAppropriateListItems(): void;

    /**
     * Getter for list item provider
     *
     * @returns BehaviorSubject<TreeNode[]>l
     */
    public getListItems(): BehaviorSubject<TreeNode[]> {
        return this.treeNodesProvider;
    }

    /**
     * Getter for SelectedListItems
     *
     * @returns BehaviorSubject<TreeNode[]>
     */
    public getSelectedListItems(): BehaviorSubject<TreeNode[]> {
        return this.selectedListItemProvider;
    }

    public filterItems(query: string): Observable<TreeNode[]> {
        return new Observable<TreeNode[]>(
            observable => {
                const filterString = query.toLocaleLowerCase();
                const filteredItems = this.treeNodes.filter(node =>
                    node.label.toLocaleLowerCase().includes(filterString)
                );
                observable.next(filteredItems);
            }
        );
    }

    /**
     * Converts Language Model array to TreeNode array and sorts alphabetically
     *
     * @param languages the models to convert
     *
     * @returns TreeNode[]
     */
    private convertDataToTreeNodes(languages: LanguageModel[]): TreeNode[] {
        const convertedArray: TreeNode[] = [];
        for (let i = 0; i < languages.length; i++) {
            convertedArray.push({
                label: this.localizationService.transform(languages[i].name),
                data: languages[i],
                type: this.languageTreeNodeType
            });
        }

        return convertedArray.sort(this.sortTreeItemsAlphabetically);
    }

    /**
     * Retrieves the existing TreeNodes that represent the available languages.
     *
     * This function is needed to work with primeng's tree component. Primeng checks if an item is
     * selected by comparing its pointer value to the available items.
     *
     * @param languages the models to map
     *
     * @returns TreeNode[]
     */
    protected mapSelectedLanguagesToNodes(languages: LanguageModel[]): TreeNode[] {
        const convertedArray: TreeNode[] = [];
        for (let i = 0; i < languages.length; i++) {
            convertedArray.push(
                this.treeNodes.find((node) => {
                    return (languages[i].id === node.data.id);
                })
            );
        }

        return convertedArray.sort(this.sortTreeItemsAlphabetically);
    }

    /**
     * Adds an item to the selected item list
     *
     * @param item the selected item
     */
    public itemSelected(item: TreeNode): void {
        this.selectedListItems.sort(this.sortTreeItemsAlphabetically);
        this.selectedListItemProvider.next(this.selectedListItems);
    }

    /**
     * Called when an item in the selectedItems list Is deleted from the
     * display poriton of the component (the x was clicked)
     *
     * @param item - item to remove from the region tree
     */
    displayItemDeleted(item: TreeNode) {
        this.itemRemoved(item);
    }

    /**
     * Removes a list item from selected list
     *
     * @param item item to be removed
     */
    public itemRemoved(item: TreeNode): void {
        const languageIdToDelete = (item.data as LanguageModel).id;

        this.selectedListItems = this.selectedListItems.filter(
            (listItem) => {
                return listItem.data.id !== languageIdToDelete;
            }
        );
        this.selectedDataItems = this.selectedListItems;
        this.updateSelectedTypes();
        this.selectedListItemProvider.next(this.selectedListItems);
        this.selectedDataItemsProvider.next(this.selectedDataItems);
    }

    /**
     * Updates the selected types and calls next on provider
     */
    updateSelectedTypes(): void {
        if (!this.selectedDataItems || !this.selectedDataItems.length) {
            this.selectedItemTypesProvider.next(null);

            return;
        }

        this.selectedItemTypesProvider.next(this.selectedItemTypes);
    }

    /**
     * Required by interface but not needed in this case
     */
    nodeExpanded(item: TreeNode) {}

    /**
     * Required by interface but not needed in this case
     */
    nodeCollapsed(item: TreeNode) {}

    /**
     * Sets the state of the service. State determines if service is changing included or excluded languages
     *
     * @param state new state of the service
     */
    public setState(state: IncludeOrExclude): void {
        this.state = state;
        this.setAppropriateListItems();
    }

    /**
     * Compare function used in sorting tree node items
     *
     * @param a item to compare
     *
     * @param b item to compare
     *
     * @returns number - either -1, 0, or 1
     */
    private sortTreeItemsAlphabetically(a: TreeNode, b: TreeNode): number {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLowerCase();

        return (labelA < labelB) ? -1 : (labelA > labelB) ? 1 : 0;
    }

    /**
     * Handles changes to selected languages
     */
    selectionChange(selectedItems: TreeNode[]): void {
        this.selectedListItems = selectedItems;
        this.selectedDataItems = selectedItems;
        this.updateSelectedTypes();
        this.selectedDataItemsProvider.next(this.selectedDataItems);
    }

    /**
     * Returns provider for selectedItemTypes
     *
     * @returns Observable<Map<string,string>>
     */
    getSelectedItemTypes(): Observable<Map<string, string>> {
        return this.selectedItemTypesProvider;
    }

    /**
     * Returns provider for selected items to display
     *
     * @returns Observable<TreeNode[]>
     */
    getSelectedItemsToDisplay(): Observable<TreeNode[]> {
        return this.selectedDataItemsProvider;
    }

    getSelectedItems(): LanguageModel[] {
        return this.selectedLanguages;
    }

    reset(): void {
        this.selectedListItems = [];
        this.selectedListItemProvider.next(this.selectedListItems);
        this.selectedDataItems = [];
        this.selectedDataItemsProvider.next(this.selectedDataItems);
    }

    // filterItems(query: string): Observable<TreeNode[]>;

    // fetchSuggestions(query: string): Observable<TreeNode[]>;

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
